import { isGE } from '../../Device'
import { useAction } from '../../utils/react-redux'
import { requestZoom } from '../../../store/map/map.actions'
import { getMapActiveDimensions } from '../map.utils'
import useTimeout from '../../utils/useTimeout'
import { noop } from '../../../utils/function'
import { MAP_ZOOM_STEP, MAP_RASTER_ZOOM_MAX, MAP_ZOOM_MIN } from '../map.constants'
import { useCallbackWithMapContext, useEffectWithMapContext } from '../MapContext'

const zoomThreshold = isGE() ? 0.9 : 0.8
const stripDecimal = val => (val - Math.floor(val) > zoomThreshold ? Math.round(val) : Math.floor(val))

export const fixZoomMethods = map => {
  const methodsToOverride = ['flyTo', 'zoomTo', 'easeTo', 'jumpTo', 'setZoom']
  methodsToOverride.forEach(methodName => {
    const originalMethod = map[methodName]
    map[`original${methodName}`] = originalMethod
    map[methodName] = (...args) => {
      const zoomAsFirstArg = typeof args[0] === 'number'
      if (zoomAsFirstArg) {
        args[0] = stripDecimal(args[0])
      } else if (args[0].zoom && typeof args[0].zoom === 'number') {
        args[0].zoom = stripDecimal(args[0].zoom)
      }
      return originalMethod.apply(map, args)
    }
  })
}

export default function useIntegerZoom() {
  const execRequestZoom = useAction(requestZoom)
  const [setTouchTimeout, clearTouchTimeout, isTouchTimeoutPending] = useTimeout(300)

  const zoomTo = useCallbackWithMapContext((map, z, options = {}) => {
    if (!options.duration) options.duration = 400
    const increase = z > map.getZoom()
    if (increase) {
      z = Math.ceil(z)
      if (z > MAP_RASTER_ZOOM_MAX) z = MAP_RASTER_ZOOM_MAX
    } else {
      z = Math.floor(z)
      if (z < MAP_ZOOM_MIN) z = MAP_ZOOM_MIN
    }
    map.zoomTo(z, options)
  })

  const getZoomOffset = useCallbackWithMapContext((map, event) => {
    const padding = map.getPadding()
    const { width, height } = getMapActiveDimensions(map)
    const centerX = width / 2 + padding.left
    const centerY = height / 2 + padding.top
    const x = event?.originalEvent?.layerX ?? centerX
    const y = event?.originalEvent?.layerY ?? centerY
    return [x - centerX, y - centerY]
  }, [])

  const handleInitialZoom = useCallbackWithMapContext(map => {
    const zoom = stripDecimal(map.getZoom())
    execRequestZoom({ zoom })
  })

  const handleWheel = useCallbackWithMapContext(
    (map, event) => {
      let delta = MAP_ZOOM_STEP
      if (event.originalEvent.deltaY > 0) delta *= -1
      const z = map.getZoom() + delta
      zoomTo(z, { offset: getZoomOffset(event) })
    },
    [getZoomOffset]
  )

  const handleDblClick = useCallbackWithMapContext((map, event) => {
    let delta = MAP_ZOOM_STEP
    if (event.originalEvent.shiftKey) delta *= -1
    zoomTo(map.getZoom() + delta, { around: event.lngLat })
  })

  const handleTouch = useCallbackWithMapContext(
    event => {
      if (!isTouchTimeoutPending()) {
        setTouchTimeout(noop)
        return
      }
      clearTouchTimeout()
      handleDblClick(event)
    },
    [isTouchTimeoutPending]
  )

  useEffectWithMapContext(map => {
    fixZoomMethods(map)
    map.once('styledata', handleInitialZoom)
    map.on('wheel', event => handleWheel(event))
    map.on('touch', event => handleTouch(event))
    map.scrollZoom.disable()
    map.doubleClickZoom.disable()
    if (isGE()) map.on('dblclick', event => handleDblClick(event))
  }, [])

  return null
}
